import { APIError, fetcher, useApplicantAPI } from "@certn/api";
import {
  showToast,
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Stack,
  Text,
} from "@certn/cascadia";
import { Loader, SectionMessage, Space } from "@certn/cascadia-react";
import { CertnDocumentScroll } from "@certn/cascadia-react/miscellaneous/contract/CertnDocumentScroll";
import {
  CascadiaControlProps,
  CascadiaJsonFormContext,
  withCascadiaControlProps,
} from "@certn/forms";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ConsentRequiredModal } from "./ConsentRequiredModal";
import { useApplication } from "../../ApplicationContext";
import { AcicConsentLayoutContext } from "../../layouts/AcicConsentLayoutContext";

export function AcicConsentControl(props: CascadiaControlProps) {
  const { t } = useTranslation();

  const { caseId } = useParams();

  const { isSubmitted, submit } = useContext(CascadiaJsonFormContext);
  const { subLabel } = useContext(AcicConsentLayoutContext);

  const { navigateToSection, prevSection, partiallySaveData } =
    useApplication();
  const [signing, setSigning] = useState(false);

  const { data: responseData, isLoading: isPdfLoading } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/acic-consent/download/`
  );
  const presignedUrl = responseData?.presigned_url || "";

  const [showConsentRequiredModal, setShowConsentRequiredModal] =
    useState(false);

  const [certnContractSigned, setCertnContractSigned] =
    useState<boolean>(false);

  useEffect(() => {
    if (isSubmitted && !certnContractSigned && !signing) {
      setShowConsentRequiredModal(true);
    }
  }, [isSubmitted, certnContractSigned, signing]);

  const handleCertnContractChange = async (isSelected: boolean) => {
    setSigning(true);
    setCertnContractSigned(true);
    props.handleChange(props.path, true);
    try {
      await fetcher.submit(
        `/api/internal/applicant/case/${caseId}/acic-consent/create-claim/`
      );
    } catch (e) {
      console.error("Error creating ACIC Consent claim", e);
      setCertnContractSigned(false);
      props.handleChange(props.path, false);
    }
    setSigning(false);
  };

  if (isPdfLoading) {
    return <Loader height="500px" />;
  }

  return (
    <Stack gap="space-4">
      <div className={styles.heading}>
        <Text as="h1" style="form/page-title" testID="acic-consent-title">
          {t("Nationally Coordinated Criminal History Consent")}
        </Text>

        {subLabel && (
          <Text
            style="form/body-primary"
            color="text-weak"
            testID="acic-consent-sub-title"
          >
            {subLabel}
          </Text>
        )}
      </div>

      <Stack gap="space-4">
        <SectionMessage
          icon="verified_user"
          sectionTitle={t("Your privacy is important to us")}
          type="success"
        >
          {t(
            "Certn will only collect information that is needed to complete your check."
          )}
        </SectionMessage>

        {presignedUrl ? (
          <CertnDocumentScroll
            presignedUrl={presignedUrl}
            isSigned={certnContractSigned}
            onChange={handleCertnContractChange}
            label={t(
              "I acknowledge and agree to the collection, use and disclosure of my personal information in accordance with the terms above."
            )}
          />
        ) : (
          <Text color="text-weak" testID="acic-contract-loading">
            {t("Loading document...")}
          </Text>
        )}
      </Stack>

      <div
        className={css(styles.actionBar, {
          borderTop: `1px solid var(--border-base)`,
        })}
      >
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={async () => {
                try {
                  await partiallySaveData();
                } catch (e) {
                  if (e instanceof APIError && e.status === 400) {
                    showToast(t("Error saving your progress"), {
                      type: "error",
                    });
                  } else {
                    showToast(t("Something went wrong, try again"), {
                      type: "error",
                    });
                    Sentry.captureException(e);
                  }
                }

                navigateToSection(prevSection);
              }}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />

          <PrimaryButton
            onPress={() => {
              submit();
            }}
            variant="brand-primary"
            isLoading={signing}
            testID="section-next-step-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </div>
      <ConsentRequiredModal
        show={showConsentRequiredModal}
        onClose={() => setShowConsentRequiredModal(false)}
      />
    </Stack>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
};

AcicConsentControl.displayName = "AcicConsentControl";

export const AcicConsentControlRenderer =
  withCascadiaControlProps(AcicConsentControl);
